/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import moment from 'moment';
import { startOfMonth, endOfMonth } from '@internationalized/date';

import {
    Button,
    Alert,
    Stack,
    Text,
    Icon,
    Box,
    STATUS,
    localToday,
    Spinner,
    useFlags,
} from 'sarsaparilla';
import { momentToCalendarDate, calendarDateToMoment } from 'sarsaparilla/moment-utils';

import { updateSelectedDate } from 'ui-search/src/actions/search';
import DatePicker from '../../components/common/DatePicker';

import {
    fetchFacilityAvailability,
    MONTHS_TO_FETCH,
} from '../../actions/facilityAvailabilityActions';
import { setDateFilter } from '../../actions/filtersActions';
import { getFarthestAvailabilityDate } from '../../utils/facilityUtils';
import { showNoTicketRequired } from '../../utils/launchDarklyUtils';
import { useAccountType } from '../../hooks/accountHooks';

export function FacilityCalendarContainer() {
    const [facilityCalendarContainerState, setFacilityCalendarContainerState] = useState({
        date: null,
    });
    const { tourIdList } = useAccountType();

    const dispatch = useDispatch();
    const { facilityId } = useParams();
    const { facilitiesUsingNoTicketRequired } = useFlags();

    const searchDate = useSelector((state) => state.search?.checkin_time); // TODO: Maybe, We could use a query param instead of redux state to get this date
    const filterDate = useSelector((state) => state.timedEntry.filters.date);
    const availabilityData = useSelector(
        (state) => state.timedEntry.facilityAvailability
    );
    const facilityData = useSelector((state) => state.timedEntry.facility);
    const { farthestByTour, lastFetched: farthestAvailabilityLastFetched } = useSelector(
        (state) => state.timedEntry.tourAvailability.farthestAvailability
    );

    const {
        isAvailableByDate,
        isNotReleasedByDate,
        isSoldOutByDate,
        isFirstComeFirstServedByDate,
        lastFetched,
        error: apiError,
        loadingStatus,
        nextReleaseByDate,
        isScheduledByDate,
    } = availabilityData;

    const { facility_timezone: timezone, loadingStatus: facilityLoadingStatus } =
        facilityData;

    const date = useMemo(() => {
        if (
            !filterDate &&
            searchDate &&
            !!Object.keys(lastFetched)?.length &&
            loadingStatus === STATUS.SUCCESS &&
            facilityLoadingStatus === STATUS.SUCCESS // We need to wait until the facility data is set into redux store
        ) {
            const formattedDate = moment(searchDate);
            dispatch(setDateFilter(formattedDate));
            setFacilityCalendarContainerState((prevState) => ({
                ...prevState,
                date: formattedDate,
            }));
            return formattedDate;
        }

        setFacilityCalendarContainerState((prevState) => ({
            ...prevState,
            date: filterDate,
        }));

        return filterDate;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterDate, searchDate, lastFetched, loadingStatus, facilityLoadingStatus]);

    const hasAvailabilityData = useMemo(() => {
        return (
            (loadingStatus === STATUS.SUCCESS ||
                (Object.values(isAvailableByDate).length > 0 &&
                    (!date || isAvailableByDate?.[date?.format('YYYY-MM')]))) &&
            !!Object.keys(lastFetched)?.length
        );
    }, [date, isAvailableByDate, lastFetched, loadingStatus]);

    const formattedDate = useMemo(() => {
        return momentToCalendarDate(facilityCalendarContainerState?.date);
    }, [facilityCalendarContainerState?.date]);

    const lastReservableDate = useMemo(() => {
        const lastResDate = getFarthestAvailabilityDate(farthestByTour, tourIdList);
        if (!lastResDate) {
            return null;
        }

        return momentToCalendarDate(lastResDate);
    }, [farthestByTour, tourIdList]);

    const maxValue = useMemo(() => {
        return lastReservableDate
            ? endOfMonth(lastReservableDate)
            : endOfMonth(localToday);
    }, [lastReservableDate]);

    const getDayType = useCallback(
        (day) => {
            if (!hasAvailabilityData) {
                return 'loading';
            }

            if (day && localToday?.compare(day) <= 0) {
                const formattedDay = day?.toString();
                const monthKey = formattedDay.slice(0, -3);
                const monthFetched = isAvailableByDate?.[monthKey];
                const countForThisDay = monthFetched?.[formattedDay];
                const notReleasedCountForThisDay =
                    isNotReleasedByDate?.[monthKey]?.[formattedDay];
                const isValidDate =
                    day.compare(localToday) >= 0 && day.compare(maxValue) <= 0;

                const monthLastFetched = lastFetched?.[monthKey];
                const soldOutForThisDay = isSoldOutByDate?.[monthKey]?.[formattedDay];
                const firstComeFirstServedForThisDay =
                    isFirstComeFirstServedByDate?.[monthKey]?.[formattedDay];
                const hasScheduledInventory =
                    isScheduledByDate?.[monthKey]?.[formattedDay];

                /**
                 * TBD - We are adding this validation since DatePicker is returning validation.isInvalid
                 * when the date is not available or when the availability has not been fetched yet
                 */
                if (isValidDate && (!monthFetched || !monthLastFetched)) {
                    return 'loading';
                }

                if (countForThisDay) {
                    return 'available';
                }

                if (notReleasedCountForThisDay) {
                    return 'notYetReleased';
                }

                if (firstComeFirstServedForThisDay) {
                    return 'firstComeFirstServed';
                }

                if (soldOutForThisDay) {
                    return 'soldOut';
                }

                if (
                    showNoTicketRequired(facilityId, facilitiesUsingNoTicketRequired) &&
                    lastReservableDate &&
                    day.compare(lastReservableDate) <= 0 &&
                    !hasScheduledInventory
                ) {
                    return 'noTicketRequired';
                }
            }

            return 'blocked';
        },
        [
            hasAvailabilityData,
            isAvailableByDate,
            isFirstComeFirstServedByDate,
            isNotReleasedByDate,
            isSoldOutByDate,
            lastFetched,
            maxValue,
            facilitiesUsingNoTicketRequired,
            facilityId,
            isScheduledByDate,
            lastReservableDate,
        ]
    );

    const isDateUnavailable = useCallback(
        (day) => {
            if (getDayType(day) === 'blocked' || getDayType(day) === 'soldOut') {
                return true;
            }

            return false;
        },
        [getDayType]
    );

    // On calendar month change pre-load the adjacent months (previous & next)
    // to the month being switched to
    const handleVisibleRangeChange = ({ start }) => {
        const nextMonth = start.add({ months: 1 });
        const prevMonth = start.subtract({ months: 1 });

        // if availability exists for the next month fetch it
        if (nextMonth?.compare(maxValue) <= 0) {
            dispatch(
                fetchFacilityAvailability({
                    facilityId,
                    date: calendarDateToMoment(nextMonth),
                    numberOfMonths: 1,
                })
            );
        }

        // if availability exists for the previous month fetch it
        if (prevMonth?.compare(startOfMonth(localToday)) >= 0) {
            dispatch(
                fetchFacilityAvailability({
                    facilityId,
                    date: calendarDateToMoment(prevMonth),
                    numberOfMonths: 1,
                })
            );
        }
    };

    const getTooltipContent = (day) => {
        if (!hasAvailabilityData) {
            return false;
        }

        if (day) {
            const formattedDay = day?.toString();
            const monthKey = formattedDay.slice(0, -3);
            const countForThisDay = isAvailableByDate?.[monthKey]?.[formattedDay];
            const notReleasedCountForThisDay =
                isNotReleasedByDate?.[monthKey]?.[formattedDay];

            if (
                !countForThisDay &&
                !!notReleasedCountForThisDay &&
                nextReleaseByDate?.[monthKey]?.[formattedDay]
            ) {
                const releaseDateAndTime = moment(
                    nextReleaseByDate?.[monthKey]?.[formattedDay],
                    'YYYY-MM-DDTHHmm'
                );
                return `Next Release: ${releaseDateAndTime.format(
                    'll'
                )} at ${releaseDateAndTime.format('hh:mm a')} ${releaseDateAndTime
                    .tz(timezone)
                    .format('z')}`;
            }
        }

        return false;
    };

    const tryFetchAvailabilityAgain = () => {
        if (facilityId) {
            dispatch(
                fetchFacilityAvailability({
                    facilityId,
                    date: moment(),
                })
            );
        }
    };

    const handleDateChange = (d, validation) => {
        const momentValue = calendarDateToMoment(d);
        setFacilityCalendarContainerState((prevState) => ({
            ...prevState,
            date: momentValue,
        }));

        if (validation?.isInvalid) {
            return;
        }

        const selectedDate = momentValue?.format('MM/DD/YYYY') ?? null;
        dispatch(setDateFilter(momentValue));
        dispatch(updateSelectedDate(selectedDate, true));
        dispatch(updateSelectedDate(selectedDate, false));
    };

    if (apiError) {
        return (
            <Alert type="error" heading="Network error" shouldFocusOnMount>
                <Stack space="sm">
                    <Text>There was a problem getting the tour availability data.</Text>

                    <Box alignX="right">
                        <Button
                            size="xs"
                            appearance="tertiary"
                            iconBeforeElement={<Icon iconName="refresh" />}
                            isLoading={loadingStatus === STATUS.PENDING}
                            onClick={tryFetchAvailabilityAgain}
                        >
                            Try again
                        </Button>
                    </Box>
                </Stack>
            </Alert>
        );
    }

    if (!hasAvailabilityData || !farthestAvailabilityLastFetched) {
        return (
            <div className="te-facility-calendar-wrap">
                <div className="te-facility-calendar-loading-indicator">
                    <div>
                        <Spinner size="xs" />
                        <Text fontWeight="semibold">Fetching availability ...</Text>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="te-facility-calendar-wrap">
            <DatePicker
                label="Add Start Date"
                value={formattedDate}
                numVisibleMonths={MONTHS_TO_FETCH}
                minValue={localToday}
                maxValue={maxValue}
                onChange={handleDateChange}
                getTooltipContent={getTooltipContent}
                getDayType={getDayType}
                onVisibleRangeChange={handleVisibleRangeChange}
                isDateUnavailable={isDateUnavailable}
            />
        </div>
    );
}
